import {
  BeatResolution,
  Key,
  MAJOR_SCALE,
  MINOR_SCALE,
  Scale,
  TICKS_PER_LOOP
} from '@arpeggi/shared'
import { RefObject, useEffect, useLayoutEffect, useRef } from 'react'
import { Arp2Utils } from '../../../../client/SongUtils'
import { useTreeState } from '../../../../stores/Root'
import { IInstrumentStemStore } from '../../../../stores/song/stem/InstrumentStemStore'
import PianoRollStateManager from './PianoRollStateManager'
import SnapManager from './SnapManager'

export const STRIPES_URL = '/static/images/piano-roll-stripes.png'
export const STRIPES_NC_URL = '/static/images/piano-roll-stripes-nonchromatic.png'
export const NOTE_HEIGHT = 16
export const NOTE_COUNT = 120 // 0 (C0) - 127 inclusive (G10), middle C is 60
export const NOTE_COUNT_NONCHROMATIC = 70
export const BACKGROUND_HEIGHT = NOTE_HEIGHT * NOTE_COUNT
export const KEYBOARD_WIDTH = 66
export const NOTE_TAIL_WIDTH = 8

const SMALL_KEYS = new Set([0, 4, 5, 11])
const BLACK_KEYS = new Set([1, 3, 6, 8, 10])

const DEFAULT_DURATION = TICKS_PER_LOOP / 8
const DEFAULT_VELOCITY = 127 // max MIDI velocity
export let savedDuration: RefObject<number> = { current: DEFAULT_DURATION } // use this to access and update duration value
let velocity: RefObject<number> = { current: DEFAULT_VELOCITY }

export const getKeyType = (key: number): 'small' | 'big' | 'black' => {
  if (key === 127) return 'small'
  if (BLACK_KEYS.has(key % 12)) return 'black'
  if (SMALL_KEYS.has(key % 12)) return 'small'
  return 'big'
}

export interface INote {
  uniqueId: string
  start: number // measured in ticks
  duration: number // measured in ticks
  note: number // 0 (C0) - 127 (G10), inclusive, where middle C is 60
  velocity: number // 1 - 127, inclusive
}

export const usePianoRollState = () => {
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const handleResize: ResizeObserverCallback = (entries) => {
      const contentRect = entries[0].contentRect
      const { width, height, top, left } = contentRect
      const x = 0
      const tickWidthPx = width / TICKS_PER_LOOP
      PianoRollStateManager.update({ width, height, top, left, x, tickWidthPx })
    }
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [containerRef])
  // pan and shit
  return containerRef
}

export const useCurrentLoop = () => {
  const { studio } = useTreeState()
  const { selectedStemIndex, selectedSection, song } = studio
  const stem = song.stems[selectedStemIndex]
  const loop = stem.getLoopAtSectionOrNull(selectedSection)
  return loop
}

export const useSnapManager = (resolution: BeatResolution) => {
  useEffect(() => {
    SnapManager.initHotKeys()
    return () => SnapManager.removeHotKeys()
  }, [])
  useEffect(() => {
    SnapManager.update({ ...SnapManager.state, resolution })
  }, [resolution])
}

export const getPianoRollHeight = (scale: Scale) =>
  (scale === Scale.CHROMATIC ? NOTE_COUNT : NOTE_COUNT_NONCHROMATIC) * NOTE_HEIGHT

export const isVisible = (noteIndex: number, scale: Scale, key: Key): boolean => {
  if (scale === Scale.MAJOR) return MAJOR_SCALE[(noteIndex - key + 12) % MAJOR_SCALE.length]
  if (scale === Scale.MINOR) return MINOR_SCALE[(noteIndex - key + 12) % MAJOR_SCALE.length]
  return true
}

export const noteToY = (note: number, scale: Scale, key: Key) => {}

export const KEYS_MAPPED = {
  KeyA: 0,
  KeyW: 1,
  KeyS: 2,
  KeyE: 3,
  KeyD: 4,
  KeyF: 5,
  KeyT: 6,
  KeyG: 7,
  KeyY: 8,
  KeyH: 9,
  KeyU: 10,
  KeyJ: 11,
  KeyK: 12,
  KeyO: 13,
  KeyL: 14
}

export const NOTE_LABELS: Record<Key, string> = {
  [Key.C]: 'C',
  [Key.C_SHARP]: 'C♯',
  [Key.D]: 'D',
  [Key.D_SHARP]: 'D♯',
  [Key.E]: 'E',
  [Key.F]: 'F',
  [Key.F_SHARP]: 'F♯',
  [Key.G]: 'G',
  [Key.G_SHARP]: 'G♯',
  [Key.A]: 'A',
  [Key.A_SHARP]: 'A♯',
  [Key.B]: 'B'
}

export const usePlayNotesFromKeyboard = ({ stem }: { stem: IInstrumentStemStore }) => {
  const { studio } = useTreeState()
  const { playSampleWithEffects } = studio

  const onKeyDown = (evt: KeyboardEvent) => {
    const activeElement = document.activeElement.tagName

    if (
      activeElement === 'INPUT' ||
      activeElement === 'TEXTAREA' ||
      KEYS_MAPPED[evt.code] === undefined
    ) {
      return
    }

    const note = (KEYS_MAPPED[evt.code] as Key) + Arp2Utils.getMiddleC()

    if (!studio.playingNotes.has(note.toString())) {
      playSampleWithEffects(stem.getCurrentSample(), stem.getStemGain(), note, undefined, true)
    }
    studio.setPlayingNoteOn(note)
  }

  const onKeyUp = (evt: KeyboardEvent) => {
    const activeElement = document.activeElement.tagName

    if (
      activeElement === 'INPUT' ||
      activeElement === 'TEXTAREA' ||
      KEYS_MAPPED[evt.code] === undefined
    ) {
      return
    }

    const note = (KEYS_MAPPED[evt.code] as Key) + Arp2Utils.getMiddleC()

    studio.setPlayingNoteOff(note)
  }

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [playSampleWithEffects, stem])
}
